<template>
  <section>
    <div class="panel-title">{{ title }}</div>

    <div class="line-separator"></div>

    <div class="row">
      <div class="col-md-12 top-margin">
         <RestrictedTwoInputs
            :fieldLabel="streetAddressLabel"
            id="ResidentialAddress"
            instructionsLabel="Make sure your address doesn't exceed 50 characters."
            primaryPlaceholder="Street and number e.g. 123 Any Street"
            secondaryPlaceholder="(optional) Apartment, building, floor, etc."
            :dual="true"
            :isDisabled="disabled"
            :primaryInput="addressO.streetAddress1"
            :secondaryInput="addressO.streetAddress2"
            @inputUpdated="residentialAddressUpdated"
            :isFirstInputRequired="true" />
      </div>
    </div>

    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <label for="zip-code">Zip code <strong class="text-info">*</strong></label>

          <input
            :disabled="disabled"
            type="text"
            name="zipCode"
            id="zipCode"
            v-model="addressO.zipCode"
            placeholder="Zip code"
            :class="{ 'form-control': true, 'form-control-error': errors.has('zipCode') && !disabled && isZipCodeInvalid(addressO.zipCode) }"
            v-validate="{required: true}" />

          <span
            v-if="errors.has('zipCode') || isZipCodeInvalid(addressO.zipCode) && !disabled"
            class="error-feedback">Required</span>
        </div>
      </div>

      <div :class="{'col-md-6': !isAdmin, 'col-md-3': isAdmin}">
        <div class="form-group">
          <label for="city">City <strong class="text-info">*</strong></label>

          <input
            :disabled="disabled"
            type="text"
            name="city"
            id="city"
            v-model="addressO.city"
            placeholder="City"
            :class="{ 'form-control': true, 'form-control-error': errors.has('city') && !disabled }"
            v-validate="{required: true}" />

          <span
            v-if="errors.has('city') && !disabled"
            class="error-feedback">Required</span>
        </div>
      </div>

      <div class="col-md-3">
        <div class="form-group">
          <label for="pwd">State <strong class="text-info">*</strong></label>

          <select
            :disabled="disabled"
            id="state"
            name="state"
            v-model="addressO.state"
            required
            :class="{ 'form-control': true, 'form-control-error': errors.has('state') && !disabled }"
            v-validate="{required: true}">

            <option value="" disabled selected hidden>State</option>

            <option
              v-for="(state, index) in countryStates"
              :key="index"
              :value="state.abbreviation">{{state.name}}</option>

          </select>

          <span v-if="errors.has('state') && !disabled" class="error-feedback">Required</span>
        </div>
      </div>

      <div v-if="isAdmin" class="col-md-3">
        <div class="form-group">
          <label for="pwd">Country <strong class="text-info">*</strong></label>

          <select
            :disabled="disabled"
            id="state"
            name="state"
            v-model="addressO.country"
            required
            :class="{ 'form-control': true, 'form-control-error': errors.has('country') && !disabled }"
            v-validate="{required: true}">

            <option value="" disabled selected hidden>Country</option>

            <option
              v-for="(country, index) in countries"
              :key="index"
              :value="country.abbreviation">{{country.name}}</option>

          </select>

          <span v-if="errors.has('country') && !disabled" class="error-feedback">Required</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import RestrictedTwoInputs from '@/components/formControls/RestrictedTwoInputs';
  import { zipRegex } from '@/util';

  export default {
    name: 'address',
    props: [
      'parentAddress',
      'states',
      'canadaStates',
      'countries',
      'title',
      'disabled',
      'validateWashington',
      'isAdmin',
      'selectedUserType',
      'isMailing',
    ],
    beforeMount() {
      this.addressO = this.parentAddress;
    },
    data() {
      return {
        addressO: {
          streetAddress1: '',
          streetAddress2: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        },
      };
    },
    components: {
      RestrictedTwoInputs,
    },
    computed: {
      countryStates() {
        const country = this.countries.find(ct => ct.abbreviation.includes('US'));
        if (country && this.addressO.country === country.abbreviation) {
          if (!this.isMailing) {
            return this.states.filter(state => !state.onlyMailingAddress);
          }
          return this.states;
        }
        return this.canadaStates;
      },
      streetAddressLabel() {
        return `Street address${this.selectedUserType !== 'INSTITUTIONAL_REPRESENTATIVE' ? '*' : ''}`;
      },
    },
    watch: {
      parentAddress(newAddress) {
        this.addressO = newAddress;
      },
    },
    methods: {
      residentialAddressUpdated($event) {
        this.addressO.streetAddress1 = $event.primary;
        this.addressO.streetAddress2 = $event.secondary;
      },
      getData() {
        return {
          streetAddress1: this.addressO.streetAddress1,
          streetAddress2: this.addressO.streetAddress2,
          city: this.addressO.city,
          state: this.addressO.state,
          zipCode: this.addressO.zipCode,
          country: this.addressO.country,
        };
      },
      isZipCodeInvalid(zipCode) {
        if (!zipCode) {
          return false;
        }
        return !zipRegex.test(zipCode);
      },
      isEmpty() {
        return this.addressO.streetAddress1.trim() === '' &&
          this.addressO.zipCode.trim() === '' &&
          this.addressO.city.trim() === '' &&
          this.addressO.state.trim() === '' &&
          this.addressO.zipCode.trim() === '';
      },
      validate() {
        return this.addressO !== null && this.addressO.streetAddress1.trim() !== '' &&
                this.addressO.zipCode.trim() !== '' &&
                this.addressO.city.trim() !== '' &&
                this.addressO.state.trim() !== '' &&
                !this.isZipCodeInvalid(this.addressO.zipCode);
      },
      setAddress(newAddress) {
        this.addressO.streetAddress1 = newAddress.streetAddress1;
        this.addressO.streetAddress2 = newAddress.streetAddress2;
        this.addressO.city = newAddress.city;
        this.addressO.state = newAddress.state;
        this.addressO.zipCode = newAddress.zipCode;
        this.addressO.country = newAddress.country;
      },
    },
  };
</script>

<style scoped>
  .main-container {
    padding-top: 5;
  }
  .notes-list-title {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .content-heading {
    font-weight: normal;
  }
  .top-margin {
    margin-top: 2%;
  }
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .panel-default {
    padding: 20px 30px;
  }
  .panel-heading {
    color: #444;
    background-color: #ffffff;
    border-color: #cfdbe2;
    border-bottom: 0;
    font-size: 14px;
    padding: 0px 5px;
  }
  table {
    border-collapse: collapse;
  }
  th {
    padding: 14px 8px;
    color: #888;
    border-bottom-width: 1px;
    vertical-align: bottom;
    border-bottom: 1px solid #eee;
    border-top: 0;
    line-height: 1.52857143;
    font-weight: normal;
  }
  td {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }
  .panel-title {
    font-size: 20px;
  }
</style>



// WEBPACK FOOTER //
// Address.vue?3ddf9d02